import { Component, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '@aveva/connect-web-core';
import { firstValueFrom } from 'rxjs';
import { MediaObserver } from '@angular/flex-layout';
import { Class } from 'src/app/models/class.model';
import { ClassesService } from 'src/app/services/classes.service';
import * as globals from '../../globals';
import { DatasetSelectorInputAsyncComponent } from '../../datasets/dataset-selector-input-async/dataset-selector-input-async.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { ConfigurationsService } from 'src/app/services/configurations.service';
import { Service } from 'src/app/models/service.model';
import { LoggingService } from '../../../services/logging.service';

@Component({
  standalone: false,
  selector: 'app-add-class',
  templateUrl: './add-class.component.html',
  styleUrls: ['./add-class.component.scss']
})
export class AddClassComponent implements OnInit {

  constructor(
    private errorHandler: ErrorHandlerService,
    private classesService: ClassesService,
    private snackBar: MatSnackBar,
    private configurationsService: ConfigurationsService,
    public media: MediaObserver,
    private logger: LoggingService) { }

  ngOnInit(): void {
    this.isContributor = ([`${globals.TC}:${globals.trainingadmin}`, `${globals.TC}:${globals.trainer}`].includes(localStorage.getItem(globals.POLICIES)));
    this.retrieveCreateServices(true);
  }

  private retrieveCreateServices(showLoadingSpinner: boolean) {
    try {
      if(showLoadingSpinner) { 
        this.loadingSpinner.emit(true);
        setTimeout(() => this.loadingSpinner.emit(true), 1000);
      }
              
      this.configurationsService.getConnectCreateServices().subscribe({
        next: (data) => {
          this.dataSource = new MatTableDataSource(data);
          this.servicesLength = data.length;
          this.dataSource.data.forEach(row => this.selection.select(row));
          this.loadingSpinner.emit(false);
        },
        error: (e) => {
          this.loadingSpinner.emit(false);
          this.logger.logException(e);
        }
      });
    }
    catch (error) {
      this.logger.logException(error);
      if(showLoadingSpinner) { 
        this.loadingSpinner.emit(false);
      }
    }
  }
  
  async create() {
    this.logger.logEvent('Class Create initialised');
    this.loadingSpinner.emit(true);
    
    if (this.submitting) {
      return;
    }

    if(this.datasetSelectedId === undefined || this.datasetSelectedId === 0) {
      this.displaySnackBar(globals.select_dataset);
      this.loadingSpinner.emit(false);
      return;
    }

    const classServicesLower = this.selection.selected.map(service => service.name.toLowerCase()).join(';');
    const datasetSelected = this.datasetSelectorInputAsyncComponent.datasets?.find(d => d.id === this.datasetSelectedId);

    if(classServicesLower.includes('datahub') && datasetSelected?.name.toLowerCase().includes('none')) {
      this.displaySnackBar(globals.dataset_cds_select);
      this.loadingSpinner.emit(false);
      return;
    }

    this.class.name = this.class.name.trim();
    this.class.dataset_id = this.datasetSelectedId;
    this.class.services = this.selection.selected.map(service => service.name).join(';');
    
    try {
      const isClassExists = await this.IsClassNameExists();    
      if(isClassExists) {
        return;
      }
  
      this.submitting = true;

      const data = await firstValueFrom(this.classesService.create_class(this.class));

      this.logger.logEvent('Class Create Success', {className: this.class.name, datasetId: this.datasetSelectedId});
      
      this.form.resetForm();
      this.loadingSpinner.emit(false);
      this.displaySnackBar(globals.classCreate_success);
      this.saved.emit(data);
    } 
    catch (error) {
      this.logger.logException(error);
      this.loadingSpinner.emit(false);
      this.displaySnackBar(globals.classCreate_fail);
      
      if (error.status === 409) {
        this.form.controls['name'].setErrors({ 'notUnique': true });
      } else if (error.error?.statusCode === 400 && error.error?.errorCode === globals.RESOURCELIMIT) {
        this.errorHandler.handle(error.error, globals.CLASS_LIMIT_REACHED);
      } else {
        return this.errorHandler.handle(error);
      }
    }
    finally {
      this.submitting = false;
    }
  }

  cancel() {
    this.form.resetForm();
    this.cancelled.emit();
    this.datasetSelectorInputAsyncComponent.clear();
  }

  close() {
    this.form.controls['name'].markAsUntouched();
    this.cancelled.emit();
    this.datasetSelectorInputAsyncComponent.clear();
  }

  private async IsClassNameExists(): Promise<boolean> {
    try {
      const isClassNameExists =  await firstValueFrom(this.classesService.isCLass_exists(0, this.class.name));
      if(isClassNameExists) {
        this.displaySnackBar(globals.className_exists);
        this.loadingSpinner.emit(false);
        return true;
      }
    }
    catch {
      return false;
    }
  }

  private isCheckBoxView = (row: Service) => (row.type === 'Required');

  datasetChanged = (datasetId: number) => this.datasetSelectedId = datasetId;

  datasetCleared = () => this.datasetSelectorInputAsyncComponent.clear();

  displaySnackBar = (message: string) => this.snackBar.open(message, null, {duration: 5000});

  submitting = false;
  class: Class = {} as Class;
  datasetSelectedId: number;
  servicesLength = 0;
  isContributor: boolean;
  dataSource = new MatTableDataSource<Service>();
  selection = new SelectionModel<Service>(true, []);
  displayedColumns: string[] = globals.services_columns;

  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Output() cancelled: EventEmitter<any> = new EventEmitter();
  @Output() loadingSpinner: EventEmitter<boolean> = new EventEmitter();
  @ViewChild(DatasetSelectorInputAsyncComponent, { static: false }) datasetSelectorInputAsyncComponent: DatasetSelectorInputAsyncComponent;
  @ViewChild('form', { static: true }) form: NgForm;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @HostListener('window:beforeunload')
  canDeactivate = (): boolean => !this.form.dirty || this.form.submitted;

}

